import { useCallback, useEffect, useMemo, useState } from 'react';
import { MeetingSlot } from '../../../lib/api';
import { getDefaultUpcomingDateLabel } from '../../../lib/helpers/dateSelectorHelper';

export type FormattedMeetingSlot = MeetingSlot & {
  startTimeEvent: string;
  endTimeEvent: string;
  startTimeLocal: string;
  endTimeLocal: string;
  date: string;
  datePillLabel: string;
};

type DateEntry = [string, FormattedMeetingSlot[]];

interface UseMeetingDateSelectorArgs {
  sortedDateEntries: DateEntry[];
  groupId: string;
}

interface UseMeetingDateSelectorResult {
  availablePillLabels: string[];
  selectedDate: string;
  showAllDates: boolean;
  showDateFilter: boolean;
  visibleDateEntries: DateEntry[];
  handleDateChange: (date: string) => void;
  handleShowAllDates: () => void;
}

export const useMeetingDateSelector = ({
  sortedDateEntries,
  groupId,
}: UseMeetingDateSelectorArgs): UseMeetingDateSelectorResult => {
  const availablePillLabels = useMemo(
    () => sortedDateEntries.map(([pillLabel]) => pillLabel),
    [sortedDateEntries]
  );

  const showDateFilter = availablePillLabels.length > 1;

  const [selectedDate, setSelectedDate] = useState(() => getDefaultUpcomingDateLabel(sortedDateEntries));
  const [showAllDates, setShowAllDates] = useState(false);

  useEffect(() => {
    setSelectedDate(getDefaultUpcomingDateLabel(sortedDateEntries));
    setShowAllDates(false);
  }, [groupId, sortedDateEntries]);

  const handleDateChange = useCallback((date: string) => {
    setSelectedDate(date);
    setShowAllDates(false);
  }, []);

  const handleShowAllDates = useCallback(() => {
    setShowAllDates((prev) => !prev);
  }, []);

  const visibleDateEntries = useMemo(() => {
    if (showAllDates) {
      return sortedDateEntries;
    }
    return sortedDateEntries.filter(([pillLabel]) => pillLabel === selectedDate);
  }, [showAllDates, sortedDateEntries, selectedDate]);

  return {
    availablePillLabels,
    selectedDate,
    showAllDates,
    showDateFilter,
    visibleDateEntries,
    handleDateChange,
    handleShowAllDates,
  };
};
